import { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Badge } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import api from '../../../api';
import DashboardCard from './DashboardCard';

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i += 1;
    }
    return `${value.toFixed(1)} ${units[i]}`;
};

const formatDuration = (hours) => {
    if (hours == null) return 'unknown';
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 48) return `${hours.toFixed(1)} h`;
    const days = hours / 24;
    if (days < 30) return `${days.toFixed(1)} days`;
    return `${(days / 30).toFixed(1)} months`;
};

const formatLastSynced = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
};

const syncSeverity = (hours) => {
    if (hours == null || hours >= 24) return { label: 'Critical', color: 'danger' };
    if (hours >= 6) return { label: 'Warning', color: 'warning' };
    return { label: 'Notice', color: 'info' };
};

const diskSeverity = (pct) => {
    if (pct >= 95) return { label: 'Critical', color: 'danger' };
    if (pct >= 90) return { label: 'High', color: 'warning' };
    return { label: 'Elevated', color: 'info' };
};

const SystemHealthCard = ({ data }) => {
    const [issueType, setIssueType] = useState(null);
    const [issues, setIssues] = useState({ syncIssues: [], lowDiskSpace: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!issueType) return;
        let cancelled = false;
        setLoading(true);
        api.get('/dashboard/system-health-issues')
            .then(res => { if (!cancelled) setIssues(res.data); })
            .catch(() => { if (!cancelled) setIssues({ syncIssues: [], lowDiskSpace: [] }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [issueType]);

    if (!data) return null;

    const { organizations } = data;
    const closeModal = () => setIssueType(null);
    const list = issueType === 'sync' ? issues.syncIssues : issues.lowDiskSpace;

    const renderSyncItem = (org) => {
        const sev = syncSeverity(org.ageHours);
        return (
            <div className="py-3 border-bottom border-secondary border-opacity-25" key={org._id}>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-1">
                    <NavLink
                        to={`/organizations/${org._id}`}
                        onClick={closeModal}
                        className="text-primary text-decoration-none fw-semibold"
                    >
                        {org.name}
                    </NavLink>
                    <Badge color={sev.color}>{sev.label}</Badge>
                </div>
                <div className="small text-light opacity-75">
                    Stale for <strong>{formatDuration(org.ageHours)}</strong>
                    {' · '}last sync {formatLastSynced(org.lastSynced)}.
                    {' '}Likely the on-site BAC server is offline or its daemon stopped.
                </div>
            </div>
        );
    };

    const renderDiskItem = (org) => {
        const sev = diskSeverity(org.percentUsed);
        const free = (org.capacity || 0) - (org.usedDiskSpace || 0);
        return (
            <div className="py-3 border-bottom border-secondary border-opacity-25" key={org._id}>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-1">
                    <NavLink
                        to={`/organizations/${org._id}`}
                        onClick={closeModal}
                        className="text-primary text-decoration-none fw-semibold"
                    >
                        {org.name}
                    </NavLink>
                    <Badge color={sev.color}>{org.percentUsed}%</Badge>
                </div>
                <div className="small text-light opacity-75">
                    {formatBytes(org.usedDiskSpace)} used of {formatBytes(org.capacity)}
                    {' · '}{formatBytes(free)} free.
                    {' '}New deliveries may fail until space is freed.
                </div>
            </div>
        );
    };

    const intro = issueType === 'sync'
        ? 'Organizations that have not reported a successful sync in over an hour. Check site connectivity and daemon status.'
        : 'Mediablocks running above 80% disk utilization. Free up space to avoid failed deliveries.';

    return (
        <>
            <DashboardCard
                title="System Health"
                icon="fas fa-server"
                action={{ to: '/organizations', label: 'View organizations' }}
            >
                <div className="row mb-3">
                    <div className="col-6">
                        <div className="text-center p-3 bg-dark rounded">
                            <div className="fs-2 fw-bold text-success">{organizations.online}</div>
                            <div className="text-light small">Online</div>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="text-center p-3 bg-dark rounded">
                            <div className="fs-2 fw-bold text-danger">{organizations.offline}</div>
                            <div className="text-light small">Offline</div>
                        </div>
                    </div>
                </div>

                <div className="text-center text-light small mb-3">
                    Total: {organizations.total} organizations
                </div>

                {organizations.syncIssues > 0 && (
                    <button
                        type="button"
                        className="alert alert-warning py-2 px-3 mb-2 small w-100 text-start border-0 d-flex justify-content-between align-items-center"
                        onClick={() => setIssueType('sync')}
                    >
                        <span>
                            <span className="fas fa-exclamation-triangle me-2" />
                            {organizations.syncIssues} with sync issues
                        </span>
                        <span className="fas fa-chevron-right" />
                    </button>
                )}

                {organizations.lowDiskSpace > 0 && (
                    <button
                        type="button"
                        className="alert alert-danger py-2 px-3 mb-2 small w-100 text-start border-0 d-flex justify-content-between align-items-center"
                        onClick={() => setIssueType('disk')}
                    >
                        <span>
                            <span className="fas fa-hdd me-2" />
                            {organizations.lowDiskSpace} with low disk space
                        </span>
                        <span className="fas fa-chevron-right" />
                    </button>
                )}
            </DashboardCard>

            <Modal isOpen={!!issueType} toggle={closeModal} scrollable size="lg">
                <ModalHeader toggle={closeModal}>
                    {issueType === 'sync' ? 'Sync Issues' : 'Low Disk Space'}
                    {list.length > 0 && (
                        <span className="ms-2 text-muted small">({list.length})</span>
                    )}
                </ModalHeader>
                <ModalBody>
                    <p className="text-light opacity-75 small mb-3">{intro}</p>
                    {loading ? (
                        <div className="text-center py-4 text-muted">Loading…</div>
                    ) : list.length === 0 ? (
                        <div className="text-center py-4 text-muted">No organizations to show.</div>
                    ) : (
                        list.map(issueType === 'sync' ? renderSyncItem : renderDiskItem)
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default SystemHealthCard;
